import { io, Socket } from 'socket.io-client'

export interface SoundNotificationPayload {
  bucket: string
  orderId: number
}

type NotificationCallback = (payload: SoundNotificationPayload) => void

class SoundNotificationService {
  private socket: Socket | null = null
  private subscribedBuckets: Set<string> = new Set()
  private callbacks: Set<NotificationCallback> = new Set()

  /** Register a callback for incoming notifications. Returns an unsubscribe fn. */
  public onNotification(callback: NotificationCallback): () => void {
    this.callbacks.add(callback)
    return () => this.callbacks.delete(callback)
  }

  public connect(baseUrl: string | undefined): void {
    if (!baseUrl) return

    if (this.socket?.connected) return

    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    this.socket = io(`${baseUrl}/sound-notification`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      auth: (cb) => cb({ token: localStorage.getItem('access_token') }),
    })

    this.socket.on('connect', () => {
      this.subscribedBuckets.forEach((bucket) => {
        this.socket?.emit('sound-alert:subscribe', { bucket })
      })
    })

    this.socket.on('sound:notification', (data: SoundNotificationPayload) => {
      this.callbacks.forEach((cb) => cb(data))
    })

    this.socket.on('connect_error', (error) => {
      console.error('[SoundNotification] Connection error:', error)
    })
  }

  public subscribe(bucket: string): void {
    this.subscribedBuckets.add(bucket)
    if (this.socket?.connected) {
      this.socket.emit('sound-alert:subscribe', { bucket })
    }
  }

  public unsubscribe(bucket: string): void {
    this.subscribedBuckets.delete(bucket)
    if (this.socket?.connected) {
      this.socket.emit('sound-alert:unsubscribe', { bucket })
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }
    this.subscribedBuckets.clear()
  }

  public isConnected(): boolean {
    return this.socket?.connected ?? false
  }
}

export const soundNotificationService = new SoundNotificationService()
